'use client';

import React, { ReactElement, useId } from 'react';
import { useTranslations } from 'next-intl';
import QuantitySelector from '@/ui/components/QuantitySelector/QuantitySelector';
import ShippingAvailabilityModal from '@/ui/components/shared/ShippingAvailabilityModal';
import { RichTextRenderer } from '@/ui/components/tileRenderers/RichTextRenderer';
import { useCurrencyFormatter } from '@/ui/hooks/useCurrencyFormatter';
import { Bundle } from '@/ui/services/BundleService';
import { ClassicPdpTileContent } from '../classic-pdp-tile-content';
import { AddToCartButton } from '../purchase-panel/add-to-cart-button';
import { ProductPrice } from '../purchase-panel/product-price';
import { ProductUsps } from '../purchase-panel/product-usps';
import { ReviewRatingSlot } from '../purchase-panel/review-rating-slot';
import { UnavailableNotice } from '../purchase-panel/unavailable-notice';
import { CLASSIC_PDP_MAX_QUANTITY, CLASSIC_PDP_MIN_QUANTITY } from '../use-classic-pdp-selection';
import { BundleSlotBlock } from './bundle-slot-block';
import { ClassicBundleSelectionState } from './use-classic-bundle-selection';

export interface BundlePurchasePanelProps {
  bundle: Bundle;
  tileContent: ClassicPdpTileContent;
  isVatNoteHidden: boolean;
  selection: ClassicBundleSelectionState;
  /**
   * Shown instead of the quantity and "Add to cart", e.g. the Nuki Club buttons. An element, not
   * any node, so a `condition && <X />` (false) cannot hide the purchase row by accident.
   */
  purchaseActions?: ReactElement;
  isAddToCartDisabled?: boolean;
}

/**
 * The translation carries the heading's full stop; it is split off so the dot
 * can take the brand colour, as headings across the shop draw it.
 */
function splitFullStop(text: string): { label: string; hasFullStop: boolean } {
  const hasFullStop = text.endsWith('.');
  return { label: hasFullStop ? text.slice(0, -1) : text, hasFullStop };
}

/**
 * Right column of the bundle page: the set's name and price, one block per
 * product of the set with its picker, then quantity and add to cart. It reuses
 * the leaf components of the product panel so both pages read the same.
 */
export function BundlePurchasePanel({
  bundle,
  tileContent,
  isVatNoteHidden,
  selection,
  purchaseActions,
  isAddToCartDisabled = false,
}: Readonly<BundlePurchasePanelProps>) {
  const t = useTranslations('BundleVariants');
  const tClassic = useTranslations('ClassicProductDetail');
  const tQuantity = useTranslations('ProductQuantityDisplay');
  const { formatCurrency } = useCurrencyFormatter();

  const {
    selected,
    selectionKey,
    selectVariant,
    quantity,
    setQuantity,
    finalPrice,
    originalPrice,
    saving,
    isAvailable,
    isShippingModalOpen,
    externalAreaLink,
    addToCart,
    notifyMe,
    closeShippingModal,
    proceedDespiteShippingWarning,
  } = selection;

  const price = formatCurrency(finalPrice);
  const totalPrice = formatCurrency(finalPrice * quantity);
  const subtitle = tileContent.subtitle || bundle.getProductNames(' + ');
  const yourSet = splitFullStop(t('yourSet'));
  const yourSetHeadingId = useId();

  return (
    <div data-testid="classic-pdp-bundle-purchase-panel" className="flex flex-col gap-6">
      {tileContent.isRatingVisible && <ReviewRatingSlot />}

      <div>
        <h1 className="text-3xl font-bold tracking-tight text-neutral-100">{bundle.name}</h1>
        {subtitle && <p className="mt-2 text-xl text-neutral-55">{subtitle}</p>}
      </div>

      {bundle.description && (
        <RichTextRenderer className="text-base text-neutral-55" blocks={bundle.description} />
      )}

      <div data-testid="classic-pdp-bundle-price" className="flex flex-wrap items-center gap-3">
        <ProductPrice price={price} isVatNoteHidden={isVatNoteHidden} />
        {saving > 0 && (
          <>
            <s className="text-lg text-neutral-55">{formatCurrency(originalPrice)}</s>
            <span className="rounded-full bg-green-700 px-3 py-1 text-sm font-bold text-white">
              {t('youSave', { amount: formatCurrency(saving) })}
            </span>
          </>
        )}
      </div>

      <section aria-labelledby={yourSetHeadingId}>
        <h2 id={yourSetHeadingId} className="text-xl">
          <span className="font-bold text-neutral-100">
            {yourSet.label}
            {yourSet.hasFullStop && <span className="text-primary-100">.</span>}
          </span>{' '}
          <span className="text-neutral-55">{t('chooseOptions')}</span>
        </h2>
        <div data-testid="classic-pdp-bundle-slots" className="mt-6 space-y-8">
          {bundle.products.map((product) => (
            <BundleSlotBlock
              key={product.bundleProductInstanceId}
              product={product}
              selectedVariant={product.resolveVariant(selected)}
              onSelect={(variant) => selectVariant(product.bundleProductInstanceId, variant)}
            />
          ))}
        </div>
      </section>

      <div className="flex flex-col gap-3">
        {!isAvailable && <UnavailableNotice />}

        {purchaseActions ?? (
          <div data-testid="classic-pdp-bundle-purchase-row" className="flex items-center gap-3">
            {/* Remounting on a new selection resets the stepper to its default. */}
            <QuantitySelector
              key={selectionKey}
              variant="pill"
              defaultQuantity={CLASSIC_PDP_MIN_QUANTITY}
              minQuantity={CLASSIC_PDP_MIN_QUANTITY}
              maxQuantity={CLASSIC_PDP_MAX_QUANTITY}
              onQuantityChange={setQuantity}
              isEditable
              decreaseLabel={tQuantity('decreaseQuantity')}
              increaseLabel={tQuantity('increaseQuantity')}
              valueLabel={tClassic('quantity')}
            />
            <AddToCartButton
              className="flex-1"
              isAvailable={isAvailable}
              onAddToCart={addToCart}
              onNotify={notifyMe}
              label={t('addToCartWithPrice', { price: totalPrice })}
              disabled={isAddToCartDisabled}
            />
          </div>
        )}

        <ProductUsps usps={tileContent.usps} />
      </div>

      {externalAreaLink && (
        <ShippingAvailabilityModal
          open={isShippingModalOpen}
          onClose={closeShippingModal}
          onTryAnyway={proceedDespiteShippingWarning}
          externalLink={externalAreaLink}
        />
      )}
    </div>
  );
}
